<template>
    <b-container id="payment-terms" v-if="$can('view-all')" fluid>
        <b-row class="page-title">
            <b-container fluid>
                <b-row>
                    <b-col cols="8">
                        <h3>Payment Terms Management</h3>
                    </b-col>
                    <b-col cols="4" class="text-right">
                        <b-button variant="primary" @click="openCreateModal" class="btn-sm">
                            <i class="fas fa-plus"></i> New Payment Term
                        </b-button>
                    </b-col>
                </b-row>
                <hr />
            </b-container>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-table
                    :items="paymentTerms"
                    :fields="fields"
                    :busy="isBusy"
                    hover
                    responsive
                    striped
                    :per-page="perPage"
                    :current-page="currentPage"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @filtered="onFiltered"
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(name)="row">
                        <strong>{{ row.item.name }}</strong>
                    </template>

                    <template v-slot:cell(sort_order)="row">
                        {{ row.item.sort_order }}
                    </template>

                    <template v-slot:cell(active)="row">
                        <b-badge :variant="row.item.active ? 'success' : 'secondary'">
                            {{ row.item.active ? 'Active' : 'Inactive' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(usage_count)="row">
                        <b-badge :variant="row.item.usage_count > 0 ? 'info' : 'light'">
                            {{ row.item.usage_count }} record(s)
                        </b-badge>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button-group size="sm">
                            <b-button variant="outline-primary" @click="editPaymentTerm(row.item)">
                                <i class="fas fa-edit"></i>
                            </b-button>
                            <b-button variant="outline-danger" @click="deletePaymentTerm(row.item)" :disabled="row.item.usage_count > 0">
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </b-button-group>
                    </template>
                </b-table>

                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="payment-terms-table"
                    class="mt-3"
                ></b-pagination>
            </b-col>
        </b-row>

        <b-modal
            :id="modalId"
            :title="modalTitle"
            @hidden="resetModal"
            @ok="savePaymentTerm"
            :ok-disabled="!isFormValid"
        >
            <b-form @submit.prevent="savePaymentTerm">
                <b-form-group label="Name" label-for="name" :invalid-feedback="errors.name">
                    <b-form-input
                        id="name"
                        v-model="form.name"
                        :state="errors.name ? false : null"
                        required
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Sort Order" label-for="sort_order" :invalid-feedback="errors.sort_order">
                    <b-form-input
                        id="sort_order"
                        v-model.number="form.sort_order"
                        type="number"
                        min="0"
                        :state="errors.sort_order ? false : null"
                    ></b-form-input>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="form.active" switch>
                        Active
                    </b-form-checkbox>
                </b-form-group>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeModal">Cancel</b-button>
                <b-button variant="primary" @click="savePaymentTerm" :disabled="!isFormValid">
                    {{ isEdit ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
export default {
    name: 'PaymentTerms',

    data() {
        return {
            paymentTerms: [],
            isBusy: false,
            perPage: 10,
            currentPage: 1,
            totalRows: 0,
            sortBy: 'sort_order',
            sortDesc: false,
            fields: [
                { key: 'name', label: 'Name', sortable: true },
                { key: 'sort_order', label: 'Sort Order', sortable: true },
                { key: 'active', label: 'Status', sortable: true },
                { key: 'usage_count', label: 'Usage', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            modalId: 'payment-term-modal',
            isEdit: false,
            form: {
                id: null,
                name: '',
                sort_order: 0,
                active: true
            },
            errors: {}
        }
    },

    computed: {
        modalTitle() {
            return this.isEdit ? 'Edit Payment Term' : 'Create Payment Term'
        },

        isFormValid() {
            return this.form.name.trim() !== ''
        }
    },

    methods: {
        async loadPaymentTerms() {
            this.isBusy = true
            try {
                const response = await axios.get('/api/company/payment-terms')
                if (response.data.status === 'success') {
                    this.paymentTerms = response.data.payment_terms
                    this.totalRows = this.paymentTerms.length
                }
            } catch (error) {
                this.showAlert('error', 'Error loading payment terms', error.response?.data?.message || 'Unknown error')
            } finally {
                this.isBusy = false
            }
        },

        openCreateModal() {
            this.isEdit = false
            this.resetForm()
            this.$bvModal.show(this.modalId)
        },

        editPaymentTerm(paymentTerm) {
            this.isEdit = true
            this.form = {
                ...paymentTerm,
                active: Boolean(paymentTerm.active)
            }
            this.$bvModal.show(this.modalId)
        },

        async savePaymentTerm() {
            this.errors = {}

            try {
                const url = this.isEdit ? `/api/company/payment-terms/${this.form.id}` : '/api/company/payment-terms'
                const method = this.isEdit ? 'put' : 'post'
                const response = await axios[method](url, this.form)

                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.closeModal()
                    this.loadPaymentTerms()
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                } else {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },

        async deletePaymentTerm(paymentTerm) {
            if (paymentTerm.usage_count > 0) {
                this.showAlert('error', 'Cannot Delete', 'This payment term is being used by existing records')
                return
            }

            if (confirm(`Are you sure you want to delete "${paymentTerm.name}"?`)) {
                try {
                    const response = await axios.delete(`/api/company/payment-terms/${paymentTerm.id}`)
                    if (response.data.status === 'success') {
                        this.showAlert('success', 'Success', response.data.message)
                        this.loadPaymentTerms()
                    }
                } catch (error) {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },

        resetForm() {
            this.form = {
                id: null,
                name: '',
                sort_order: 0,
                active: true
            }
            this.errors = {}
        },

        closeModal() {
            this.$bvModal.hide(this.modalId)
            this.resetForm()
        },

        resetModal() {
            this.resetForm()
        },

        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length
            this.currentPage = 1
        },

        showAlert(type, title, message) {
            this.$bvToast.toast(message, {
                title: title,
                variant: type,
                solid: true,
                autoHideDelay: 5000
            })
        }
    },

    mounted() {
        this.loadPaymentTerms()
    }
}
</script>

<style scoped>
#payment-terms {
    margin-top: 80px;
}

.page-title {
    margin-bottom: 20px;
}

.table th {
    border-top: none;
}

.badge {
    font-size: 0.75em;
}

.btn-group .btn {
    margin-right: 2px;
}

.btn-group .btn:last-child {
    margin-right: 0;
}
</style>
